/*
  Product video interaction.

  The design has no control bar: the video is a piece of the gallery, and the
  only thing to do with it is stop and start it. So the whole frame is the
  control — tap or click anywhere on it to pause, again to resume — and a play
  mark is drawn over it while it is paused, so a stopped video never looks like
  a broken one.

  Nothing here renders the video. Dawn's <deferred-media> still clones it out of
  its <template> on the poster click; this watches for that and configures what
  comes out, which is why it works for the gallery and the zoom modal alike
  without either of them knowing about it.

  Why an observer rather than patching DeferredMedia.loadContent: that method is
  Dawn's and is called from several places (poster click, variant change, modal
  open). Watching for the element is one rule that covers all of them and
  survives Dawn being updated.
*/
(function productVideo() {
  const PAUSED = 'tyo-video--paused';

  /*
    playsinline is the important one on iOS: without it Safari takes any
    playing video fullscreen with its own controls, which is the opposite of
    what this is for. It cannot be set through Liquid's media_tag, so it is set
    here, before the first frame.
  */
  function configure(video) {
    if (video.dataset.tyoBound) return;
    video.dataset.tyoBound = 'true';

    video.controls = false;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    // Nothing to right-click into a download menu.
    video.setAttribute('controlsList', 'nodownload noplaybackrate');
    video.setAttribute('disablepictureinpicture', '');

    const frame = video.closest('deferred-media') || video.parentElement;
    if (!frame) return;

    /*
      Muted unless the section says otherwise. DeferredMedia calls play() as
      soon as it appends the element, so this has to be set here rather than
      waited for — a beat of sound before muting is worse than no control.
    */
    if (frame.dataset.startMuted !== 'false') video.muted = true;

    const paint = () => frame.classList.toggle(PAUSED, video.paused);
    video.addEventListener('play', paint);
    video.addEventListener('pause', paint);
    paint();

    sound(frame, video);

    /*
      The listener is on the frame, not the video: the paused overlay sits on
      top of the video, and a tap that landed on it would otherwise do nothing.
    */
    frame.addEventListener('click', (event) => {
      // The poster button is a control of its own — it loads the video.
      if (event.target.closest('.deferred-media__poster')) return;
      toggle(video);
    });

    /*
      A div is not focusable and a video without controls is not either, so the
      frame is given a role and a tab stop. Space and Enter then do what the tap
      does, which is the whole interaction.
    */
    frame.setAttribute('tabindex', '0');
    frame.setAttribute('role', 'button');
    frame.addEventListener('keydown', (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      toggle(video);
    });
  }

  /*
    The sound button.

    The button lives in the markup already; this only connects it. State is kept
    on the video itself and read back through `volumechange`, so the icon is
    right even if something else mutes it — pauseAllMedia on a variant change,
    or the shopper's own OS controls.
  */
  /*
    The two shapes the one mark takes: waves when sound is on, a cross when it
    is off. Redrawing a single path means the icon cannot end up showing both
    states at once, which is what two SVGs toggled by CSS did the moment those
    rules lost.
  */
  const MARK_ON = 'M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12';
  const MARK_OFF = 'M16.5 9.5l5 5m0-5l-5 5';

  function sound(frame, video) {
    const button = frame.querySelector('[data-sound-toggle]');
    if (!button) return;

    const mark = button.querySelector('[data-sound-mark]');

    const paint = () => {
      const on = !video.muted && video.volume > 0;
      button.setAttribute('aria-pressed', String(on));
      if (mark) mark.setAttribute('d', on ? MARK_ON : MARK_OFF);
      // Says what the click will DO, not what the state is — a button labelled
      // with its current state reads as a promise to keep it that way.
      const label = on ? button.dataset.labelMute : button.dataset.labelUnmute;
      if (label) button.setAttribute('aria-label', label);
    };

    video.addEventListener('volumechange', paint);
    paint();

    button.addEventListener('click', (event) => {
      // The frame is the play/pause control and this button sits on top of it.
      // Without this, unmuting would also pause.
      event.stopPropagation();
      video.muted = !video.muted;
      // Unmuting a video whose volume was dragged to zero would do nothing
      // visible, so give it something to play.
      if (!video.muted && video.volume === 0) video.volume = 1;
      paint();
    });
  }

  function toggle(video) {
    if (video.paused) {
      const played = video.play();
      // Safari rejects this if the gesture is not trusted; a paused video is a
      // fine outcome, an unhandled rejection in the console is not.
      if (played && played.catch) played.catch(() => {});
    } else {
      video.pause();
    }
  }

  function scan(root) {
    root.querySelectorAll('deferred-media video').forEach(configure);
  }

  function start() {
    scan(document);

    /*
      Videos arrive later in three ways: the poster click clones one in, the
      modal clones the gallery again, and a variant change re-renders the whole
      media list. One observer on the body catches all three.
    */
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'VIDEO') {
            configure(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
